import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, FileCheck2, ListChecks, Play, UserCheck, Wrench } from "lucide-react";
import { useApi, useInvalidateOn } from "@ncp/api-client";
import { formatDateTime, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, InlineAlert, Skeleton, Tooltip, cn, toast, useSave } from "@ncp/ui";
import { completenessKey, completenessStatusMeta, isActionable, type CompletenessView } from "./completeness";
import { ResolveChecklistItemDialog } from "./ResolveChecklistItemDialog";
import { useStaffName } from "@/lib/staff-names";
import { FigureInfo } from "./MethodInfo";
import { measureThreshold, useCalculationMethod } from "./figures";

export type { CompletenessItem, CompletenessStatus, CompletenessView } from "./completeness";

/**
 * "Where does it show me what's missing?" — the Article 10 / MOE/CD-F-04 checklist for the file as it stands now, with the Initial Review
 * Agent's evidence (document, page, request text) overlaid when it has run, and the officer's overrides on top of both. Rows link to
 * the document viewer; every row can be resolved in place (upload, re-file a document, or record a ruling).
 */
export function CompletenessPanel({ caseId, onOpenDocument, onOpenProposals }: { caseId: string; onOpenDocument: (documentId: string) => void; onOpenProposals: () => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const staffName = useStaffName();
  const api = useApi();
  const ar = locale === "ar";
  useInvalidateOn(["case.updated", "proposal.created", "proposal.updated", "agent.run"], [[...completenessKey(caseId)]]);
  const q = useQuery({ queryKey: completenessKey(caseId), queryFn: () => api.get<CompletenessView>(`/api/cases/${caseId}/completeness`) });
  // The score is read in the published method's band (condition 2.16), never one written here.
  const method = useCalculationMethod();
  const fairFrom = measureThreshold(method.data, "completeness", "completenessFair");
  const run = useSave({ mutationFn: () => api.post(`/api/ai/cases/${caseId}/run`, { agentKey: "formal-review" }), onSuccess: () => toast.info(t("backoffice:workspace.runAgent"), { description: t("backoffice:workspace.formalReviewQueued") }) });
  const [resolving, setResolving] = useState<string | null>(null);

  if (q.isLoading) return <Skeleton className="h-64" />;
  if (!q.data) return null;
  const d = q.data;
  const actionable = d.items.filter(isActionable);
  const overridden = d.items.filter((i) => i.override).length;
  const tone = d.score == null || fairFrom == null ? "text-muted-foreground" : d.complete ? "text-success-600" : d.score >= fairFrom ? "text-warning-600" : "text-danger-600";
  const fr = d.formalReview;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-2 text-base"><ListChecks className="size-4 text-aegold-700" /> {t("backoffice:workspace.completeness")}</CardTitle>
            <CardDescription className="mt-0.5">{ar ? d.basisAr : d.basis}</CardDescription>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-end">
              {d.score == null
                ? <p className="text-sm text-muted-foreground">{t("backoffice:r3Metrics.notRecorded")}</p>
                : <p className={cn("inline-flex items-center gap-1 text-2xl font-semibold font-heading tabular leading-none", tone)}>{d.score}<span className="text-sm text-muted-foreground">/100</span><FigureInfo measure="completeness" inputs={[t("backoffice:workspace.requiredDocumentsOnFile", { present: d.mandatoryPresent, total: d.mandatoryTotal }), d.findings.length ? t("backoffice:r3Metrics.openFindings", { count: d.findings.length }) : null]} /></p>}
              <p className="text-[11px] text-muted-foreground">{t("backoffice:workspace.requiredDocumentsOnFile", { present: d.mandatoryPresent, total: d.mandatoryTotal })}{d.scoreAtSubmission != null && d.scoreAtSubmission !== d.score ? <> · {t("backoffice:workspace.atSubmission")} {d.scoreAtSubmission}<FigureInfo measure="filingReadiness" className="ms-0.5 inline-grid size-4 align-middle" inputs={[t("backoffice:workspace.readinessAtSubmission")]} /></> : null}</p>
            </div>
            <Badge variant={d.complete ? "success" : actionable.length ? "danger" : "warning"} dot>{d.complete ? t("backoffice:workspace.fileComplete") : d.score == null ? t("backoffice:r3Metrics.notRecorded") : t("backoffice:workspace.itemsToResolve", { count: actionable.length + d.findings.length })}</Badge>
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {fr ? (
            <>
              <FileCheck2 className="size-3.5 text-seablue-600" />
              <span>{d.determinedBy === "Officer" && d.determinedByName
                ? t("backoffice:r3Metrics.determinedByOfficer", { name: staffName(d.determinedByName), date: d.determinedAt ? formatDateTime(d.determinedAt, locale) : "" })
                : t("backoffice:r3Metrics.proposedByAgent", { agent: ar && fr.agentNameAr ? fr.agentNameAr : fr.agentName, date: formatDateTime(fr.createdAt, locale) })}</span>
              {fr.status === "Proposed" ? <Button size="sm" variant="ghost" className="-my-1 h-7" onClick={onOpenProposals}>{t("backoffice:workspace.reviewProposal")} <ExternalLink /></Button> : <Badge size="sm" variant="outline">{t(`enums.proposalStatus.${fr.status}`, { defaultValue: fr.status })}</Badge>}
            </>
          ) : (
            <>
              <span>{t("backoffice:workspace.readinessOnly")}</span>
              <Button size="sm" variant="ghost" className="-my-1 h-7" loading={run.isPending} onClick={() => run.mutate()}><Play /> {t("backoffice:workspace.runFormalReview")}</Button>
            </>
          )}
          {overridden > 0 ? <span className="ms-auto inline-flex items-center gap-1 text-aegold-800"><UserCheck className="size-3.5" /> {t("backoffice:workspace.overriddenCount", { count: overridden })}</span> : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {(ar ? d.findingsAr : d.findings).length ? (
          <InlineAlert tone="warning" title={t("backoffice:workspace.consistencyFindings")}>
            <ul className="mt-1 list-disc space-y-0.5 ps-4">{(ar ? d.findingsAr : d.findings).map((f) => <li key={f}>{f}</li>)}</ul>
          </InlineAlert>
        ) : null}
        <ul className="divide-y divide-sand-100 rounded-xl border border-sand-200">
          {d.items.map((i) => {
            const meta = completenessStatusMeta[i.status] ?? completenessStatusMeta.Missing;
            const Icon = meta.icon;
            const finding = ar ? i.findingAr : i.finding;
            const request = ar && i.requestTextAr ? i.requestTextAr : i.requestText;
            const todo = isActionable(i);
            return (
              <li key={i.key} className={cn("group flex items-start gap-3 px-3 py-2.5", i.override && "bg-aegold-50/30")}>
                <Icon className={cn("mt-0.5 size-4 shrink-0", meta.tone)} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-medium">{ar ? i.titleAr : i.titleEn}</p>
                    {!i.mandatory ? <Badge size="sm" variant="outline">{t("backoffice:workspace.optional")}</Badge> : null}
                    {i.source === "FormalReview" ? <Badge size="sm" variant="ai">{t("common:ai.badge")}</Badge> : null}
                    {i.override ? (
                      <Tooltip content={<span>{i.override.reason}<br /><span className="opacity-70">{staffName(i.override.setByName)} · {formatDateTime(i.override.setAt, locale)}</span></span>}>
                        <Badge size="sm" variant="gold"><UserCheck /> {t("backoffice:workspace.overrideBadge")}</Badge>
                      </Tooltip>
                    ) : null}
                  </div>
                  {i.override ? <p className="text-xs text-aegold-900/80" dir="auto">{i.override.reason}</p> : finding ? <p className="text-xs text-muted-foreground">{finding}</p> : null}
                  {request && todo ? <p className="mt-1 rounded-lg bg-warning-50 px-2 py-1 text-xs text-warning-900"><span className="font-semibold">{t("backoffice:workspace.requestText")}:</span> {request}</p> : null}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {i.documentId ? (
                    <button type="button" onClick={() => onOpenDocument(i.documentId!)} className="max-w-44 truncate text-xs text-aegold-800 underline-offset-2 hover:underline" title={i.fileName ?? undefined}>
                      {i.fileName ?? t("backoffice:workspace.openDocument")}{i.page ? ` · ${t("common:ai.pageRef", { n: i.page })}` : ""}
                    </button>
                  ) : null}
                  {/* Only an exception is tagged (visual item 13): a document on file says so with its check mark alone. */}
                  {i.status === "Present" ? <span className="sr-only">{t("backoffice:workspace.completenessStatus.Present")}</span> : <Badge size="sm" variant={meta.badge}>{t(`backoffice:workspace.completenessStatus.${i.status}`, { defaultValue: i.status })}</Badge>}
                  {todo ? (
                    <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => setResolving(i.key)}><Wrench /> {t("backoffice:workspace.resolveItem")}</Button>
                  ) : (
                    <Tooltip content={t("backoffice:workspace.resolveItemHint")}>
                      <Button size="icon-sm" variant="ghost" className="size-7 text-sand-500 opacity-60 group-hover:opacity-100 focus-visible:opacity-100" aria-label={t("backoffice:workspace.resolveItem")} onClick={() => setResolving(i.key)}><Wrench className="size-3.5" /></Button>
                    </Tooltip>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
        {actionable.length > 0 && fr?.status === "Proposed" && fr.proposedActionType === "rfi.create" ? (
          <p className="text-xs text-muted-foreground">{t("backoffice:workspace.deficiencyDrafted")} <Button size="sm" variant="outline" className="ms-2 h-7" onClick={onOpenProposals}>{t("backoffice:workspace.reviewProposal")}</Button></p>
        ) : null}
      </CardContent>
      <ResolveChecklistItemDialog caseId={caseId} itemKey={resolving} open={resolving !== null} onOpenChange={(o) => { if (!o) setResolving(null); }} />
    </Card>
  );
}
